import type { NpcConfig } from '../../types/npc';
import type { SceneDocument } from '../../types/scene';
import type { Simulation } from '../../types/simulation';
import { DEFAULT_WANDER_MOVEMENT_CONFIG } from '../../lib/npcMovement';
import { cloneFallbackProviders } from '../../lib/llmConfig';

const DEFAULT_SCOPED_MOVEMENT_CONFIG: NpcConfig['movementConfig'] = {
  mode: 'wander',
  speedProfile: 'normal',
  wander: {
    ...DEFAULT_WANDER_MOVEMENT_CONFIG,
  },
};

const toScopedSimulationNpcConfig = (member: Simulation['cast'][number]): NpcConfig => {
  const wander = DEFAULT_SCOPED_MOVEMENT_CONFIG.wander;
  if (!wander) {
    throw new Error('Expected default scoped movement configuration to include wander settings.');
  }

  return {
    id: member.npcId,
    placementId: member.placementId,
    source: 'scoped',
    sourceNpcId: member.scopedNpcId ?? member.sourceNpcId,
    name: member.name,
    title: member.title,
    expertise: member.expertise,
    personality: member.personality,
    backstory: member.backstory,
    greeting: member.greeting,
    spriteId: member.spriteId,
    avatarLayers: member.avatarLayers,
    spriteColors: member.spriteColors,
    spawnX: member.spawnX ?? 0,
    spawnY: member.spawnY ?? 0,
    zone: member.zone ?? 'default',
    llmProvider: member.llmProvider,
    llmModel: member.llmModel,
    fallbackProviders: cloneFallbackProviders(member.fallbackProviders),
    temperature: member.temperature,
    maxTokens: member.maxTokens,
    movementConfig: {
      ...DEFAULT_SCOPED_MOVEMENT_CONFIG,
      wander: {
        ...wander,
      },
    },
    waypointPath: [],
    agentConfig: null,
  };
};

export const mergeScopedSimulationNpcConfigs = (params: {
  baseNpcConfigs: NpcConfig[];
  scene: SceneDocument | null;
  activeSimulation: Simulation | null;
}): NpcConfig[] => {
  const { activeSimulation, baseNpcConfigs, scene } = params;
  if (!scene || !activeSimulation || activeSimulation.sceneId !== scene.id) {
    return baseNpcConfigs;
  }

  const mergedById = new Map<string, NpcConfig>();
  baseNpcConfigs.forEach((npc) => mergedById.set(npc.id, npc));
  activeSimulation.cast
    .filter((member) => member.source === 'scoped')
    .forEach((member) => {
      mergedById.set(member.npcId, toScopedSimulationNpcConfig(member));
    });
  return [...mergedById.values()];
};
